import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

// Average Return — two modes: Average & Cumulative Return (list of annual returns), or Based on Cash Flow
export default function AverageReturnCalc() {
  const [mode, setMode] = useState('returns');
  return (
    <CalcShell look="cnet" title="Average Return Calculator" subtitle="Compute average annual return + cumulative return from a series of yearly returns or cash flows."
      about={<><p>Arithmetic vs. geometric mean returns behave differently. This calculator shows both plus the cumulative return.</p></>}
    >
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        <button onClick={()=>setMode('returns')} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${mode==='returns'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`}>Average and Cumulative Return</button>
        <button onClick={()=>setMode('cash')} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${mode==='cash'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`}>Average Return Based on Cash Flow</button>
      </div>
      {mode === 'returns' ? <ReturnsMode/> : <CashFlowMode/>}
    </CalcShell>
  );
}

function ReturnsMode() {
  const [returns, setReturns] = useState([10, -5, 15, 20, 8]);
  const update = (i, v) => setReturns(prev => prev.map((r, idx) => idx === i ? v : r));
  const addYear = () => setReturns(prev => [...prev, 0]);
  const c = useMemo(() => {
    const arith = returns.reduce((a,b)=>a+b, 0) / returns.length;
    const cum = returns.reduce((acc, r) => acc * (1 + r/100), 1);
    const geo = (Math.pow(cum, 1/returns.length) - 1) * 100;
    return { arith, geo, cumulative: (cum - 1) * 100 };
  }, [returns]);
  return (
    <div>
      <table className="w-full text-sm mb-4">
        <thead className="bg-slate-100"><tr><th className="px-2 py-2 text-left">Year</th><th className="px-2 py-2 text-right">Return (%)</th></tr></thead>
        <tbody>
          {returns.map((r, i) => (<tr key={i} className="border-b"><td className="px-2 py-2">Year {i+1}</td><td className="px-2 py-2"><div className="flex gap-1 justify-end"><input type="number" step="0.01" value={r} onChange={e=>update(i, +e.target.value||0)} className="!py-1 w-32 text-right"/><span>%</span></div></td></tr>))}
        </tbody>
      </table>
      <button onClick={addYear} className="text-blue-700 text-sm font-semibold">+ Add another year</button>
      <table className="data-table mt-6"><tbody>
        <tr><td>Arithmetic average (simple)</td><td className="text-right font-semibold" data-testid="ar-arith">{c.arith.toFixed(3)}%</td></tr>
        <tr><td>Geometric average (compound)</td><td className="text-right font-semibold text-blue-700">{c.geo.toFixed(3)}%</td></tr>
        <tr><td>Cumulative return</td><td className="text-right font-semibold">{c.cumulative.toFixed(3)}%</td></tr>
      </tbody></table>
    </div>
  );
}

function CashFlowMode() {
  const [rows, setRows] = useState([
    { date: '2023-01-01', amount: 5500 },
    { date: '2024-01-01', amount: 5000 },
    { date: '2024-05-01', amount: -1700 },
    { date: '2025-01-01', amount: 3800 },
  ]);
  const [currentValue, setCurrentValue] = useState(17000);
  const [currentDate, setCurrentDate] = useState('2026-01-01');

  const update = (i, k, v) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows(prev => [...prev, { date: prev[prev.length-1]?.date || '2026-01-01', amount: 0 }]);
  const removeRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));

  const c = useMemo(() => {
    // Build cash-flow series relative to earliest date
    const flows = [...rows.filter(r => r.amount !== 0), { date: currentDate, amount: -currentValue }]
      .map(r => ({ t: new Date(r.date).getTime(), amount: r.amount }))
      .sort((a, b) => a.t - b.t);
    if (flows.length < 2) return { irr: 0, points: [] };
    const t0 = flows[0].t;
    const years = f => (f.t - t0) / (365.25 * 24 * 3600 * 1000);
    // IRR: solve for r where sum(cf_i / (1+r)^t_i) = 0. Newton's method.
    const npv = r => flows.reduce((acc, f) => acc + f.amount / Math.pow(1 + r, years(f)), 0);
    let r = 0.1;
    for (let i = 0; i < 100; i++) {
      const eps = 1e-5;
      const grad = (npv(r + eps) - npv(r - eps)) / (2 * eps);
      if (Math.abs(grad) < 1e-10) break;
      const next = r - npv(r) / grad;
      if (!isFinite(next)) break;
      if (Math.abs(next - r) < 1e-8) { r = next; break; }
      r = Math.max(-0.999, Math.min(10, next));
    }
    return { irr: r * 100, points: flows.filter(f => Math.abs(f.amount) > 0 && f.amount !== -currentValue).map(f => ({ date: new Date(f.t), amount: f.amount })) };
  }, [rows, currentValue, currentDate]);

  const maxAbs = Math.max(1, ...c.points.map(p => Math.abs(p.amount)));

  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">Average Return Based on Cash Flow</h3>
      <p className="text-sm text-slate-600 mb-4">Compute the effective annual return (IRR) from a series of deposits and withdrawals.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">Date</th>
                <th className="px-2 py-2 text-right">Amount ($)</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-200">
                  <td className="px-2 py-1.5">{i + 1}</td>
                  <td className="px-2 py-1.5"><input type="date" value={r.date} onChange={e=>update(i, 'date', e.target.value)} className="w-full border border-slate-400 rounded px-1 py-1 text-sm" data-testid={`ar-cf-date-${i}`}/></td>
                  <td className="px-2 py-1.5"><input type="number" step="0.01" value={r.amount} onChange={e=>update(i, 'amount', +e.target.value||0)} className="w-full border border-slate-400 rounded px-1 py-1 text-sm text-right" data-testid={`ar-cf-amt-${i}`}/></td>
                  <td className="px-2 py-1.5 text-center"><button onClick={()=>removeRow(i)} className="text-red-600 text-xs">×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={addRow} className="mt-2 text-blue-700 text-sm font-semibold" data-testid="ar-cf-add">+ Add Cash Flow</button>
          <div className="mt-4 field"><label>Current Value on</label>
            <div className="flex gap-2">
              <input type="date" value={currentDate} onChange={e=>setCurrentDate(e.target.value)} className="flex-1"/>
              <span className="self-center">$</span>
              <input type="number" value={currentValue} onChange={e=>setCurrentValue(+e.target.value||0)} className="flex-1" data-testid="ar-cf-current"/>
            </div>
          </div>
        </div>

        <div>
          <div className="cnet-result-hdr">Result</div>
          <div className="cnet-result-body">
            <p className="text-slate-700 mb-4">The average return for the investment is <b className="text-green-700 text-lg" data-testid="ar-cf-irr">{c.irr.toFixed(2)}% per year</b>.</p>

            {/* Bar chart of deposits/withdrawals */}
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Deposits and Withdrawals</h4>
            <svg viewBox="0 0 400 200" className="w-full border border-slate-200 rounded bg-white">
              <line x1={40} y1={100} x2={390} y2={100} stroke="#94a3b8" strokeWidth="1"/>
              <line x1={40} y1={20}  x2={390} y2={20}  stroke="#e2e8f0" strokeDasharray="3 3"/>
              <line x1={40} y1={180} x2={390} y2={180} stroke="#e2e8f0" strokeDasharray="3 3"/>
              <text x={36} y={24}  textAnchor="end" fontSize="10" fill="#64748b">+${(maxAbs/1000).toFixed(0)}k</text>
              <text x={36} y={104} textAnchor="end" fontSize="10" fill="#64748b">0</text>
              <text x={36} y={184} textAnchor="end" fontSize="10" fill="#64748b">−${(maxAbs/1000).toFixed(0)}k</text>
              {c.points.length > 0 && (() => {
                const t0 = c.points[0].date.getTime();
                const tN = c.points[c.points.length-1].date.getTime();
                const range = Math.max(1, tN - t0);
                return c.points.map((p, i) => {
                  const x = 40 + ((p.date.getTime() - t0) / range) * 350;
                  const h = (Math.abs(p.amount) / maxAbs) * 75;
                  const y = p.amount > 0 ? 100 - h : 100;
                  return <rect key={i} x={x - 8} y={y} width={16} height={h} fill={p.amount > 0 ? '#2563eb' : '#dc2626'}/>;
                });
              })()}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
